import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import AuditPopover from '../components/AuditPopover';
import QuickListManager from '../components/QuickListManager';
import DataTable from '../components/DataTable';
import AttachmentManager from '../components/AttachmentManager';
import CustomFieldsSection from '../components/CustomFieldsSection';
import { TabIcon } from '../utils/tabIcons';
import { Button, Modal, FormField, EmptyState, ListPageHeader, ListPageStats, FilterBar } from '../components/ui';
import { CardHero, CardStatusBadge, CardTabs, CardSplitButton } from '../components/card';

const IMG_BASE = window.location.hostname === 'localhost' ? 'http://localhost:5001' : '';

const DEPTS = ['Satış', 'Muhasebe', 'Depo', 'Üretim', 'Yonetim', 'IT', 'Lojistik', 'Diğer'];
const emptyPerson = {
  code:'', first_name:'', last_name:'', id_number:'', birth_date:'', gender:'',
  phone:'', mobile:'', email:'', address:'', city:'', district:'',
  department:'', position:'', hire_date:'', salary:0, official_salary:0,
  is_sales_rep:false, is_warehouse_staff:false, is_driver:false, is_plasiyer:false,
  notes:'', is_active:true, group_id:null, branch_id:'',
  is_terminated:false, termination_date:'', termination_reason:'',
  commission_type:'', commission_rate_default:'', dist_share_pct:''
};

function PersonnelPage({ autoOpenNew = false, onSaved = null, onNavigate = null }) {
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({...emptyPerson});
  const [isEditing, setIsEditing] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [imgUploading, setImgUploading] = useState(false);
  const imgInputRef = useRef(null);
  const [groups, setGroups] = useState([]);
  const [branches, setBranches] = useState([]);
  const [showGroupMgr, setShowGroupMgr] = useState(false);
  const [movements, setMovements] = useState([]);
  const [movementsLoading, setMovementsLoading] = useState(false);
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({ department: '', position: '', is_active: '' });

  const fetchGroups = useCallback(async () => {
    try {
      const r = await api.get('/personnel/groups');
      if (r.data.success) setGroups(r.data.data || []);
    } catch {}
  }, []);
  const fetchBranches = useCallback(async () => {
    try {
      const r = await api.get('/branches', { params: { is_active: 'yes' } });
      if (r.data.success) setBranches(r.data.data.items || []);
    } catch {}
  }, []);
  useEffect(() => { fetchGroups(); fetchBranches(); }, [fetchGroups, fetchBranches]);

  const fetchMovements = useCallback(async (id) => {
    if (!id) return;
    setMovementsLoading(true);
    try {
      const r = await api.get('/personnel/' + id + '/movements');
      if (r.data.success) setMovements(r.data.data || []);
      else setMovements([]);
    } catch (e) { console.error(e); setMovements([]); }
    finally { setMovementsLoading(false); }
  }, []);
  useEffect(() => {
    if (showModal && activeTab === 'movements' && selected?.id) fetchMovements(selected.id);
  }, [showModal, activeTab, selected?.id, fetchMovements]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { search };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get('/personnel', { params });
      if (res.data.success) { setList(res.data.data.items); setTotal(res.data.data.total); }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search, filters]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const fetchStats = useCallback(async () => {
    try {
      const r = await api.get('/personnel/stats');
      if (r.data.success) setStats(r.data.data);
    } catch {}
  }, []);
  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => {
    if (autoOpenNew) {
      setSelected(null); setForm({...emptyPerson}); setIsEditing(true); setIsNew(true);
      setActiveTab('general'); setMsg(null); setShowModal(true);
    }
  }, [autoOpenNew]);

  const handleSelect = async (p) => {
    try {
      const res = await api.get('/personnel/' + p.id);
      if (res.data.success) {
        const d = res.data.data;
        if (d.birth_date) d.birth_date = d.birth_date.substring(0,10);
        if (d.hire_date) d.hire_date = d.hire_date.substring(0,10);
        setSelected(d); setForm(d); setIsEditing(false); setIsNew(false); setActiveTab('general'); setMsg(null);
        setShowModal(true);
      }
    } catch (err) { console.error(err); }
  };

  const handleNew = () => {
    setSelected(null); setForm({...emptyPerson}); setIsEditing(true); setIsNew(true);
    setActiveTab('general'); setMsg(null); setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false); setSelected(null); setForm({...emptyPerson});
    setIsEditing(false); setIsNew(false); setMsg(null);
  };

  const handleEdit = () => { setIsEditing(true); setMsg(null); };
  const handleCancel = () => {
    if (isNew) { handleModalClose(); }
    else { setForm(selected); setIsEditing(false); setMsg(null); }
  };

  const handleSave = async (mode = 'close') => {
    if (mode && typeof mode === 'object') mode = 'close';
    if (!form.first_name || !form.last_name) { setMsg({ type:'error', text:'Ad ve soyad zorunludur (kod boş bırakılabilir, otomatik üretilir)' }); return; }
    setSaving(true); setMsg(null);
    try {
      if (isNew) {
        const res = await api.post('/personnel', form);
        if (res.data.success) {
          setMsg({ type:'success', text:'Personel olusturuldu' }); setIsNew(false); setIsEditing(false);
          fetchList(); handleSelect({ id: res.data.data.id });
          if (onSaved) onSaved(res.data.data);
        }
      } else {
        const res = await api.put('/personnel/' + selected.id, form);
        if (res.data.success) { setMsg({ type:'success', text:'Personel guncellendi' }); setIsEditing(false); fetchList(); }
      }
      // Post-save mode handling
      if (mode === 'close') {
        setTimeout(() => handleModalClose(), 250);
      } else if (mode === 'new') {
        setTimeout(() => {
          setSelected(null); setForm({ ...emptyPerson });
          setIsEditing(true); setIsNew(true); setActiveTab('general'); setMsg(null);
        }, 250);
      } else if (mode === 'copy') {
        setTimeout(() => {
          const copy = { ...form, id: undefined, code: '', id_number: '', phone: '', email: '' };
          setSelected(null); setForm(copy);
          setIsEditing(true); setIsNew(true); setActiveTab('general'); setMsg(null);
        }, 250);
      }
    } catch (err) { setMsg({ type:'error', text: err.response?.data?.message || 'Hata' }); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!selected || !window.confirm('Silmek istediginize emin misiniz?')) return;
    try {
      await api.delete('/personnel/' + selected.id);
      handleModalClose(); fetchList();
    } catch(e) {
      if (e.response?.status === 409 && e.response.data?.hasMovements) {
        setMsg({ type: 'error', text: e.response.data.message || 'Önce bu kayda bağlı kayıtları kaldırın' });
      } else { setMsg({ type: 'error', text: e.response?.data?.message || 'Silme hatası' }); }
    }
  };

  const f = (key, val) => setForm(prev => ({...prev, [key]: val}));

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selected?.id) return;
    setImgUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await api.post('/personnel/' + selected.id + '/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data.success) { const updated = { ...selected, image_url: res.data.image_url }; setSelected(updated); setForm(updated); fetchList(); }
    } catch (err) { setMsg({ type: 'error', text: 'Resim yüklenemedi' }); }
    finally { setImgUploading(false); if (imgInputRef.current) imgInputRef.current.value = ''; }
  };

  const handleImageDelete = async () => {
    if (!selected?.id || !window.confirm('Resmi silmek istiyor musunuz?')) return;
    try {
      await api.delete('/personnel/' + selected.id + '/image');
      const updated = { ...selected, image_url: null }; setSelected(updated); setForm(updated); fetchList();
    } catch (err) { setMsg({ type: 'error', text: 'Resim silinemedi' }); }
  };

  const tabs = [
    { key:'general',   label:'Genel',         icon:'general' },
    { key:'contact',   label:'İletişim',      icon:'phone' },
    { key:'job',       label:'İş Bilgileri',  icon:'briefcase' },
    { key:'movements', label:'Hareketler',    icon:'activity', requireSaved: true },
    { key:'photo',     label:'Fotoğraf',      icon:'image' },
    { key:'custom',    label:'Özel',           icon:'sparkles', requireSaved: true },
    { key:'documents', label:'Belgeler',      icon:'paperclip' },
    { key:'notes',     label:'Notlar',        icon:'notes' }
  ].filter(t => !t.requireSaved || !isNew);

  const movementRows = (() => {
    let bal = 0;
    return movements.map(m => {
      const cr = parseFloat(m.credit) || 0;
      const db = parseFloat(m.debit) || 0;
      bal = bal + cr - db;
      return { ...m, debitNum: db, creditNum: cr, balance: bal };
    });
  })();
  const totalDebit = movementRows.reduce((s,r) => s + r.debitNum, 0);
  const totalCredit = movementRows.reduce((s,r) => s + r.creditNum, 0);
  const fmtMoney = (n) => new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Math.abs(n || 0));
  const fmtMovDate = (d) => d ? new Date(d).toLocaleDateString('tr-TR') : '';

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="Personel"
        count={total}
        unit="kayit"
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Kod, ad veya telefon ara..."
        onRefresh={() => { fetchList(); fetchStats(); }}
        refreshing={loading}
        actions={<Button size="sm" onClick={handleNew}>+ Yeni Personel</Button>}
      />

      <ListPageStats
        loading={stats == null}
        stats={[
          {
            key: 'active',
            label: 'Aktif Personel',
            value: stats?.total_active,
            delta: stats?.new_30d > 0 ? stats.new_30d : null,
            deltaTooltip: 'Son 30 günde eklenen',
            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
          },
          {
            key: 'inactive',
            label: 'Pasif',
            value: stats?.inactive,
            color: 'neutral',
            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="17" y1="8" x2="22" y2="13"/><line x1="22" y1="8" x2="17" y2="13"/></svg>,
            onClick: () => setFilters(f => ({ ...f, is_active: 'no' })),
          },
          {
            key: 'dept',
            label: 'Departmanlı',
            value: stats?.with_dept,
            color: 'primary',
            sublabel: 'departman atanmış',
            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>,
          },
          {
            key: 'new',
            label: 'Yeni (30 gün)',
            value: stats?.new_30d,
            color: 'success',
            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="10" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>,
          },
        ]}
      />

      <FilterBar
        show={showFilterBar}
        onToggle={() => setShowFilterBar(s => !s)}
        values={filters}
        onChange={setFilters}
        fields={[
          { key: 'department', type: 'select', label: 'Departman', flex: '1 1 200px', minWidth: 180,
            options: [
              { value: '', label: 'Tüm Departmanlar' },
              ...Array.from(new Set(list.map(p => p.department).filter(Boolean))).sort().map(d => ({ value: d, label: d })),
            ] },
          { key: 'position', type: 'select', label: 'Pozisyon', flex: '1 1 200px', minWidth: 180,
            options: [
              { value: '', label: 'Tüm Pozisyonlar' },
              ...Array.from(new Set(list.map(p => p.position).filter(Boolean))).sort().map(p => ({ value: p, label: p })),
            ] },
          { key: 'is_active', type: 'select', label: 'Durum', minWidth: 140,
            options: [
              { value: '', label: 'Tümü' },
              { value: 'yes', label: 'Aktif' },
              { value: 'no', label: 'Pasif' },
            ] },
        ]}
      />

      {/* TABLO */}
      <div className="stocks-table-wrap">
        <DataTable
          pageKey="personnel-list"
          tableClassName="stocks-table"
          rowClassName="stocks-table-row"
          loading={loading}
          emptyText="Personel bulunamadı"
          data={list}
          rowKey="id"
          onRowClick={p => handleSelect(p)}
          bulkActions={[
            {
              key: 'activate', label: 'Aktif Yap', variant: 'ghost',
              onClick: async (ids, clear) => {
                try { const r = await api.post('/personnel/bulk', { action: 'set_active', ids });
                  if (r.data.success) { clear(); fetchList(); fetchStats(); }
                } catch (e) { alert(e.response?.data?.message || e.message); }
              },
            },
            {
              key: 'deactivate', label: 'Pasif Yap', variant: 'ghost',
              confirm: 'Seçili personel pasif yapılacak. Emin misiniz?',
              onClick: async (ids, clear) => {
                try { const r = await api.post('/personnel/bulk', { action: 'set_inactive', ids });
                  if (r.data.success) { clear(); fetchList(); fetchStats(); }
                } catch (e) { alert(e.response?.data?.message || e.message); }
              },
            },
            {
              key: 'set_group', label: 'Grup Ata', variant: 'primary',
              prompt: {
                type: 'select', title: 'Toplu Personel Grubu Atama',
                description: 'Seçili personellere atanacak grubu seçin:',
                label: 'Grup',
                options: (groups || []).map(g => ({ value: g.id, label: g.name })),
                allowEmpty: true, emptyLabel: '— Grubu kaldır —',
              },
              onClick: async (ids, clear, value) => {
                try { const r = await api.post('/personnel/bulk', { action: 'set_group', ids, value });
                  if (r.data.success) { clear(); fetchList(); }
                } catch (e) { alert(e.response?.data?.message || e.message); }
              },
            },
            {
              key: 'set_department', label: 'Departman Ata', variant: 'primary',
              prompt: {
                type: 'text', title: 'Toplu Departman Atama',
                description: 'Seçili personellere atanacak departman adını girin:',
                label: 'Departman', placeholder: 'örn: Satış, Üretim, Muhasebe',
                allowEmpty: true, emptyLabel: '— Departmanı kaldır —',
              },
              onClick: async (ids, clear, value) => {
                try { const r = await api.post('/personnel/bulk', { action: 'set_department', ids, value });
                  if (r.data.success) { clear(); fetchList(); fetchStats(); }
                } catch (e) { alert(e.response?.data?.message || e.message); }
              },
            },
            {
              key: 'set_position', label: 'Pozisyon Ata', variant: 'primary',
              prompt: {
                type: 'text', title: 'Toplu Pozisyon Atama',
                description: 'Seçili personellere atanacak pozisyonu girin:',
                label: 'Pozisyon', placeholder: 'örn: Uzman, Müdür, Şef',
                allowEmpty: true, emptyLabel: '— Pozisyonu kaldır —',
              },
              onClick: async (ids, clear, value) => {
                try { const r = await api.post('/personnel/bulk', { action: 'set_position', ids, value });
                  if (r.data.success) { clear(); fetchList(); }
                } catch (e) { alert(e.response?.data?.message || e.message); }
              },
            },
            {
              key: 'delete', label: 'Sil', variant: 'danger',
              confirm: 'Seçili personel SİLİNECEK. Hareketi olanlar silinemez. Devam edilsin mi?',
              onClick: async (ids, clear) => {
                try { const r = await api.post('/personnel/bulk', { action: 'delete', ids });
                  if (r.data.success) { clear(); fetchList(); fetchStats(); }
                } catch (e) { alert(e.response?.data?.message || e.message); }
              },
            },
          ]}
          columns={[
            { id: 'code',     header: 'Personel Kodu', accessor: 'code', sortable: true, width: 130,
              cell: p => <span className="stock-code">{p.code}</span> },
            { id: 'fullname', header: 'Ad Soyad', accessor: 'full_name', sortable: true,
              cell: p => <span className="stock-name-cell">{p.full_name}</span> },
            { id: 'dept',     header: 'Departman', accessor: 'department', sortable: true,
              cell: p => <span className="stock-secondary">{p.department || '-'}</span> },
            { id: 'pos',      header: 'Pozisyon', accessor: 'position', sortable: true,
              cell: p => <span className="stock-secondary">{p.position || '-'}</span> },
            { id: 'role',     header: 'Rol', accessor: p => p.is_sales_rep ? 'Satış Tem.' : 'Personel', sortable: true,
              headerStyle: { textAlign: 'center' }, cellStyle: { textAlign: 'center' }, width: 110,
              cell: p => (
                <span className="stock-badge active" style={{ background: p.is_sales_rep ? 'rgba(139,92,246,0.1)' : 'rgba(16,185,129,0.1)', color: p.is_sales_rep ? 'var(--accent-primary)' : 'var(--success)' }}>
                  {p.is_sales_rep ? 'Satış Tem.' : 'Personel'}
                </span>
              ) },
            { id: 'balance',  header: 'Bakiye', accessor: p => parseFloat(p.balance) || 0, sortable: true, width: 140,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right', fontFamily: 'monospace', whiteSpace: 'nowrap' },
              cell: p => {
                const bal = parseFloat(p.balance) || 0;
                if (bal === 0) return <span style={{ color: 'var(--text-tertiary)' }}>0,00</span>;
                return (
                  <span style={{ fontWeight: 600, color: bal > 0 ? '#16a34a' : '#dc2626' }}>
                    {fmtMoney(bal)} {bal > 0 ? 'A' : 'B'}
                  </span>
                );
              } },
            { id: 'active',   header: 'Durum', accessor: p => p.is_active ? 'Aktif' : 'Pasif', sortable: true,
              headerStyle: { textAlign: 'center' }, cellStyle: { textAlign: 'center' }, width: 90,
              cell: p => <span className={'stock-badge ' + (p.is_active ? 'active' : 'passive')}>{p.is_active ? 'Aktif' : 'Pasif'}</span> },
            { id: 'audit',    header: '', sortable: false, hideable: false, draggable: false, filterable: false, resizable: false, width: 32,
              headerStyle: { textAlign: 'center' }, cellStyle: { textAlign: 'center' },
              cell: p => <span onClick={e => e.stopPropagation()}><AuditPopover endpoint={`/personnel/${p.id}/audit`} /></span> },
          ]}
        />
      </div>

      {/* DETAY MODAL */}
      {showModal && (
        <Modal
          icon={null}
          title={isNew ? 'Yeni Personel' : 'Personel Kartı'}
          size="xl"
          onClose={handleModalClose}
          footer={
            <>
              <div className="ui-modal-footer-left">
                {!isEditing && !isNew && <Button variant="danger" onClick={handleDelete}>🗑 Sil</Button>}
              </div>
              <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {isEditing && <Button variant="ghost" onClick={handleCancel}>{isNew ? 'Vazgeç' : 'İptal'}</Button>}
                {isEditing && (
                  <CardSplitButton
                    primaryLabel="💾 Kaydet"
                    primaryAction={() => handleSave('close')}
                    loading={saving}
                    options={[
                      { label: 'Kaydet ve Yeni Aç',  icon: '✚', onClick: () => handleSave('new') },
                      { label: 'Kaydet ve Kopyala', icon: '⎘', onClick: () => handleSave('copy') },
                      { label: 'Kaydet ve Kapat',   icon: '✓', onClick: () => handleSave('close') },
                    ]}
                  />
                )}
                {!isEditing && !isNew && <Button onClick={handleEdit}>✎ Düzenle</Button>}
              </div>
            </>
          }
        >
          {/* Hero strip */}
          {!isNew && (
            <CardHero
              icon="👤"
              code={form.code}
              title={form.full_name || ((form.first_name || '') + ' ' + (form.last_name || '')).trim()}
              subtitle={[form.department, form.position].filter(Boolean).join(' · ')}
              statusBadge={<CardStatusBadge preset={form.is_active === false ? 'inactive' : 'active'} />}
              kpis={[
                form.salary > 0 && { label: 'Maaş', value: form.salary, color: 'success', sublabel: form.currency_code || 'TRY' },
                form.start_date && { label: 'İşe Giriş', value: new Date(form.start_date).toLocaleDateString('tr-TR'), color: 'neutral' },
                form.phone && { label: 'Telefon', value: form.phone, color: 'info' },
                form.id_number && { label: 'TC No', value: form.id_number, color: 'neutral' },
              ].filter(Boolean)}
            />
          )}

          {msg && <div className={'detail-msg '+msg.type} style={{ marginBottom: 12 }}>{msg.text}</div>}

          <CardTabs
            items={tabs.map(t => ({ key: t.key, label: t.label, icon: <TabIcon name={t.icon}/> }))}
            active={activeTab}
            onChange={setActiveTab}
          />

          {/* Eski .detail-tabs (gizli, içerik var) */}
          <div className="detail-tabs" style={{ display: 'none' }}>
            {tabs.map(t=>(<button key={t.key} className={'tab-btn'+(activeTab===t.key?' active':'')} onClick={()=>setActiveTab(t.key)}><TabIcon name={t.icon}/><span>{t.label}</span></button>))}
          </div>
          <div className="detail-body">
              {activeTab === 'general' && (
                <div className="form-grid">
                  <div className="form-row"><label>Personel Kodu *</label><input value={form.code||''} onChange={e=>f('code',e.target.value)} disabled={!isEditing}/></div>
                  <div className="form-row"><label>Ad *</label><input value={form.first_name||''} onChange={e=>f('first_name',e.target.value)} disabled={!isEditing}/></div>
                  <div className="form-row"><label>Soyad *</label><input value={form.last_name||''} onChange={e=>f('last_name',e.target.value)} disabled={!isEditing}/></div>
                  <div className="form-row"><label>TC Kimlik No</label><input value={form.id_number||''} onChange={e=>f('id_number',e.target.value)} disabled={!isEditing}/></div>
                  <div className="form-row"><label>Dogum Tarihi</label><input type="date" value={form.birth_date||''} onChange={e=>f('birth_date',e.target.value)} disabled={!isEditing}/></div>
                  <div className="form-row"><label>Cinsiyet</label><select value={form.gender||''} onChange={e=>f('gender',e.target.value)} disabled={!isEditing}><option value="">Seciniz</option><option value="Erkek">Erkek</option><option value="Kadin">Kadin</option></select></div>
                  <div className="form-row"><label>Durum</label><select value={form.is_active?'1':'0'} onChange={e=>f('is_active',e.target.value==='1')} disabled={!isEditing}><option value="1">Aktif</option><option value="0">Pasif</option></select></div>
                </div>
              )}
              {activeTab === 'contact' && (
                <div className="form-grid">
                  <div className="form-row"><label>Telefon</label><input value={form.phone||''} onChange={e=>f('phone',e.target.value)} disabled={!isEditing}/></div>
                  <div className="form-row"><label>Cep Telefonu</label><input value={form.mobile||''} onChange={e=>f('mobile',e.target.value)} disabled={!isEditing}/></div>
                  <div className="form-row"><label>E-posta</label><input value={form.email||''} onChange={e=>f('email',e.target.value)} disabled={!isEditing}/></div>
                  <div className="form-row"><label>Sehir</label><input value={form.city||''} onChange={e=>f('city',e.target.value)} disabled={!isEditing}/></div>
                  <div className="form-row"><label>Ilce</label><input value={form.district||''} onChange={e=>f('district',e.target.value)} disabled={!isEditing}/></div>
                  <div className="form-row full"><label>Adres</label><textarea rows="3" value={form.address||''} onChange={e=>f('address',e.target.value)} disabled={!isEditing}/></div>
                </div>
              )}
              {activeTab === 'job' && (
                <div className="form-grid">
                  <div className="form-row"><label>Departman</label><select value={form.department||''} onChange={e=>f('department',e.target.value)} disabled={!isEditing}><option value="">Seciniz</option>{DEPTS.map(d=><option key={d} value={d}>{d}</option>)}</select></div>
                  <div className="form-row"><label>Grup {isEditing && <button type="button" className="quick-add-btn" onClick={()=>setShowGroupMgr(true)}>+</button>}</label><select value={form.group_id||''} onChange={e=>f('group_id',e.target.value||null)} disabled={!isEditing}><option value="">Seciniz</option>{groups.map(g=><option key={g.id} value={g.id}>{g.code?g.code+' - ':''}{g.name}</option>)}</select></div>
                  <div className="form-row"><label>Pozisyon</label><input value={form.position||''} onChange={e=>f('position',e.target.value)} disabled={!isEditing}/></div>
                  <div className="form-row"><label>Bağlı Şube</label><select value={form.branch_id||''} onChange={e=>f('branch_id',e.target.value||null)} disabled={!isEditing}><option value="">Seciniz</option>{branches.map(b=><option key={b.id} value={b.id}>{b.code?b.code+' - ':''}{b.name}</option>)}</select></div>
                  <div className="form-row"><label>Ise Baslama</label><input type="date" value={form.hire_date||''} onChange={e=>f('hire_date',e.target.value)} disabled={!isEditing}/></div>
                  <div className="form-row"><label>Maas</label><input type="number" step="0.01" value={form.salary||0} onChange={e=>f('salary',parseFloat(e.target.value)||0)} disabled={!isEditing}/></div>
                  <div className="form-row"><label>Resmi Maaş</label><input type="number" step="0.01" value={form.official_salary||0} onChange={e=>f('official_salary',parseFloat(e.target.value)||0)} disabled={!isEditing}/></div>
                  <div className="form-row checkbox-row"><label><input type="checkbox" checked={!!form.is_sales_rep} onChange={e=>f('is_sales_rep',e.target.checked)} disabled={!isEditing}/> Satış Temsilcisi</label></div>
                  <div className="form-row checkbox-row"><label><input type="checkbox" checked={!!form.is_warehouse_staff} onChange={e=>f('is_warehouse_staff',e.target.checked)} disabled={!isEditing}/> Depo Personeli</label></div>
                  <div className="form-row checkbox-row"><label><input type="checkbox" checked={!!form.is_driver} onChange={e=>f('is_driver',e.target.checked)} disabled={!isEditing}/> Sofor</label></div>
                  <div className="form-row checkbox-row"><label><input type="checkbox" checked={!!form.is_plasiyer} onChange={e=>f('is_plasiyer',e.target.checked)} disabled={!isEditing}/> Plasiyer (Saha)</label></div>
                  <div className="form-row" style={{gridColumn:'1/-1',marginTop:8,paddingTop:12,borderTop:'1px dashed var(--border-secondary)',fontWeight:600,fontSize:'0.85rem',color:'var(--text-secondary)'}}>Prim Ayarları</div>
                  <div className="form-row">
                    <label>Prim Tipi</label>
                    <select value={form.commission_type||''} onChange={e=>f('commission_type',e.target.value||null)} disabled={!isEditing}>
                      <option value="">— Yok —</option>
                      <option value="sale">Satış (fatura kesilince tahakkuk)</option>
                      <option value="collection">Tahsilat (para gelince tahakkuk)</option>
                    </select>
                  </div>
                  <div className="form-row">
                    <label>Varsayılan Oran (%)</label>
                    <input type="number" step="0.01" min={0} max={100} value={form.commission_rate_default||''} onChange={e=>f('commission_rate_default',e.target.value)} disabled={!isEditing} placeholder="Stok kartında oran yoksa kullanılır"/>
                  </div>
                  <div className="form-row">
                    <label>Şube Hedef Bonusu Dağıtım Payı (%)</label>
                    <input type="number" step="0.01" min={0} max={100} value={form.dist_share_pct||''} onChange={e=>f('dist_share_pct',e.target.value)} disabled={!isEditing} placeholder="Örn. 35 (şubedeki personellerin toplamı 100 olmalı)"/>
                  </div>
                  <div className="form-row" style={{gridColumn:'1/-1',marginTop:8,paddingTop:12,borderTop:'1px solid var(--border-secondary)'}}>
                    <label style={{display:'flex',gap:6,alignItems:'center',fontWeight:600,color:'var(--text-primary)'}}>
                      <input type="checkbox" checked={!!form.is_terminated} onChange={e=>f('is_terminated',e.target.checked)} disabled={!isEditing}/>
                      İşten Ayrıldı
                    </label>
                  </div>
                  {form.is_terminated && (<>
                    <div className="form-row"><label>İşten Ayrılış Tarihi</label><input type="date" value={form.termination_date||''} onChange={e=>f('termination_date',e.target.value)} disabled={!isEditing}/></div>
                    <div className="form-row full"><label>Ayrılma Sebebi</label><textarea rows="2" value={form.termination_reason||''} onChange={e=>f('termination_reason',e.target.value)} disabled={!isEditing} placeholder="İstifa, performans, anlaşma vs."/></div>
                  </>)}
                </div>
              )}
              {activeTab === 'photo' && (
                <div style={{padding:'16px 0'}}>
                  {form.image_url ? (
                    <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:16}}>
                      <div className="img-preview-wrap">
                        <img src={IMG_BASE + form.image_url} alt="Personel fotografi" style={{width:200,height:200,objectFit:'cover',borderRadius:'50%'}} />
                        {!isNew && <button className="img-remove-btn" title="Fotografi sil" onClick={handleImageDelete}>✕</button>}
                      </div>
                      {!isNew && <Button variant="ghost" size="sm" onClick={() => imgInputRef.current?.click()} loading={imgUploading}>Fotografı Değiştir</Button>}
                    </div>
                  ) : (
                    isNew ? (
                      <div style={{textAlign:'center',color:'var(--text-tertiary)',padding:24,fontSize:'0.85rem'}}>Personeli kaydettikten sonra fotograf yukleyebilirsiniz.</div>
                    ) : (
                      <div className="img-upload-area" onClick={() => imgInputRef.current?.click()}>
                        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                        <span>{imgUploading ? 'Yükleniyor...' : 'Fotograf yuklemek icin tiklayın'}</span>
                        <small>JPG, PNG, WEBP — maks 5 MB</small>
                      </div>
                    )
                  )}
                  <input ref={imgInputRef} type="file" accept="image/*" style={{display:'none'}} onChange={handleImageUpload} />
                </div>
              )}
              {activeTab === 'movements' && (
                <div style={{padding:'8px 0'}}>
                  {movementsLoading ? (
                    <div style={{padding:24,textAlign:'center',color:'var(--text-tertiary)'}}>Yükleniyor...</div>
                  ) : movementRows.length === 0 ? (
                    <EmptyState title="Hareket yok" description="Bu personele ait hareket bulunamadı" compact />
                  ) : (
                    <div style={{overflowX:'auto'}}>
                      <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.83rem'}}>
                        <thead style={{background:'var(--bg-secondary)'}}>
                          <tr>
                            <th style={{width:32}}></th>
                            <th style={{padding:'8px 10px',textAlign:'left',whiteSpace:'nowrap'}}>Tarih</th>
                            <th style={{padding:'8px 10px',textAlign:'left'}}>Açıklama</th>
                            <th style={{padding:'8px 10px',textAlign:'left'}}>Belge</th>
                            <th style={{padding:'8px 10px',textAlign:'right'}}>Borç</th>
                            <th style={{padding:'8px 10px',textAlign:'right'}}>Alacak</th>
                            <th style={{padding:'8px 10px',textAlign:'right'}}>Bakiye</th>
                          </tr>
                        </thead>
                        <tbody>
                          {movementRows.map((m,idx) => {
                            const openLinkedDoc = () => {
                              if (!onNavigate || !m.source_id) return;
                              // Otomatik üretilmiş kayıt: kaynak belgeye git
                              if (m.source_type === 'expense_voucher') {
                                handleModalClose();
                                onNavigate('expense-vouchers', m.source_id);
                                return;
                              }
                              if (m.source_type === 'invoice_sale') {
                                handleModalClose();
                                onNavigate('sales', m.source_id);
                                return;
                              }
                              // Manuel personnel_transactions: borç/alacak dekontuna git
                              const targetPage = m.creditNum > 0 ? 'personnel-alacak' : 'personnel-borc';
                              handleModalClose();
                              onNavigate(targetPage, m.source_id);
                            };
                            return (
                            <tr key={idx} style={{borderBottom:'1px solid var(--border-secondary)'}}>
                              <td style={{padding:'4px 6px',textAlign:'center'}}>
                                {m.source_id && onNavigate && (
                                  <button onClick={openLinkedDoc} title="Belgeyi aç"
                                    style={{background:'transparent',border:'1px solid var(--border-secondary)',borderRadius:6,padding:'3px 6px',cursor:'pointer',color:'var(--accent-primary)'}}>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                                  </button>
                                )}
                              </td>
                              <td style={{padding:'7px 10px',whiteSpace:'nowrap'}}>{fmtMovDate(m.date)}</td>
                              <td style={{padding:'7px 10px'}}>{m.description}</td>
                              <td style={{padding:'7px 10px',fontFamily:'monospace',fontSize:'0.78rem',color:'var(--text-tertiary)'}}>{m.doc_no || '—'}</td>
                              <td style={{padding:'7px 10px',textAlign:'right',fontFamily:'monospace',color: m.debitNum>0 ? '#dc2626' : 'var(--text-tertiary)'}}>{m.debitNum>0 ? fmtMoney(m.debitNum) : '—'}</td>
                              <td style={{padding:'7px 10px',textAlign:'right',fontFamily:'monospace',color: m.creditNum>0 ? '#16a34a' : 'var(--text-tertiary)'}}>{m.creditNum>0 ? fmtMoney(m.creditNum) : '—'}</td>
                              <td style={{padding:'7px 10px',textAlign:'right',fontFamily:'monospace',fontWeight:600,color: m.balance>0 ? '#16a34a' : m.balance<0 ? '#dc2626' : 'var(--text-secondary)'}}>
                                {fmtMoney(m.balance)} {m.balance>0 ? 'A' : m.balance<0 ? 'B' : ''}
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                        <tfoot style={{background:'var(--bg-secondary)',fontWeight:600}}>
                          <tr>
                            <td style={{padding:'8px 10px'}} colSpan={4}>Toplam</td>
                            <td style={{padding:'8px 10px',textAlign:'right',fontFamily:'monospace',color:'#dc2626'}}>{fmtMoney(totalDebit)}</td>
                            <td style={{padding:'8px 10px',textAlign:'right',fontFamily:'monospace',color:'#16a34a'}}>{fmtMoney(totalCredit)}</td>
                            <td style={{padding:'8px 10px',textAlign:'right',fontFamily:'monospace',color: (totalCredit-totalDebit)>0 ? '#16a34a' : (totalCredit-totalDebit)<0 ? '#dc2626' : 'var(--text-secondary)'}}>
                              {fmtMoney(totalCredit - totalDebit)} {(totalCredit-totalDebit)>0 ? 'A' : (totalCredit-totalDebit)<0 ? 'B' : ''}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>
              )}
              {activeTab === 'custom' && selected?.id && (
                <CustomFieldsSection
                  entityType="personnel"
                  entityId={selected.id}
                  autoSave
                  disabled={!isEditing}
                />
              )}
              {activeTab === 'documents' && (
                <AttachmentManager entityType="personnel" entityId={selected?.id} />
              )}
              {activeTab === 'notes' && (
                <div className="form-grid"><div className="form-row full"><label>Notlar</label><textarea rows="6" value={form.notes||''} onChange={e=>f('notes',e.target.value)} disabled={!isEditing}/></div></div>
              )}
          </div>
        </Modal>
      )}

      {showGroupMgr && (
        <QuickListManager open={true} title="Personel Grubu"
          items={groups} apiBase="/personnel/groups"
          onClose={()=>setShowGroupMgr(false)} onChanged={fetchGroups} />
      )}
    </div>
  );
}
export default PersonnelPage;
